import { getAccessToken, setBearerToken } from 'axiosConfig'
import { PropsWithChildren } from 'global'
import { SigninRedirectArgs, User, UserManager } from 'oidc-client-ts'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import NotAuthorizedPage from 'shared/components/NotAuthorizedPage'
import PageLoader from 'shared/components/PageLoader'
import { CHANNEL_LOGOUT } from 'shared/constants/broadcastChannels'
import { LOCAL_STORAGE_REDIRECT_URL_LOGOUT } from 'shared/constants/localStorageVariables'
import { useBroadcast } from 'shared/hooks/useBroadcast'
import {
  AuthProviderProps,
  BblSignoutRedirectArgs,
} from '../shared/models/Context'
import { hasCodeInUrl } from '../shared/utils/codeInUrl'
import { AuthContext } from './context'

/**
 * @private
 * @hidden
 * @param props
 */
const initUserManager = (props: AuthProviderProps): UserManager => {
  if (props.userManager) return props.userManager

  const {
    authority,
    client_id,
    client_secret,
    redirect_uri,
    silent_redirect_uri,
    post_logout_redirect_uri,
    response_type,
    scope,
    automaticSilentRenew,
    loadUserInfo,
    popupRedirectUri,
    popupWindowTarget,
    userStore,
  } = props

  return new UserManager({
    authority,
    client_id: client_id,
    client_secret: client_secret,
    redirect_uri: redirect_uri,
    silent_redirect_uri: silent_redirect_uri || redirect_uri,
    post_logout_redirect_uri: post_logout_redirect_uri || redirect_uri,
    response_type: response_type || 'code',
    scope: scope || 'openid',
    loadUserInfo: loadUserInfo !== undefined ? loadUserInfo : true,
    popup_redirect_uri: popupRedirectUri,
    popupWindowTarget: popupWindowTarget,
    automaticSilentRenew,
    userStore,
  })
}

/**
 *
 * @param props AuthProviderProps
 */
export const AuthProvider: FC<AuthProviderProps & PropsWithChildren> = ({
  children,
  autoSignIn = true,
  onBeforeSignIn,
  onSignIn,
  onSignOut,
  location = window.location,
  ...props
}) => {
  const [isLoading, setIsLoading] = useState(true)
  const [userData, setUserData] = useState<User | null>()
  const [userManager] = useState<UserManager>(() => initUserManager(props))
  const { send, subscribe } = useBroadcast<boolean>(CHANNEL_LOGOUT, false, {
    subscribe: true,
  })

  const signOutHooks = async (): Promise<void> => {
    setUserData(null)
    onSignOut?.()
    send(true)
  }

  const isAuthenticated = () => {
    if (!userData || !userData.expires_at) return false

    const expires = userData.expires_at * 1000
    const now = new Date().getTime()
    return !!userData.access_token && expires > now
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleRefreshToken = userManager.events.addAccessTokenExpiring((e) => {
    setTimeout(() => {
      const token = getAccessToken()
      setBearerToken(token)
    }, 500) // Wait for new token to be set in localstorage
  })

  const isMountedRef = useRef(true)
  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    const sub = subscribe(async (isLoggedOut) => {
      if (!isLoggedOut) return

      const user = await userManager?.getUser()

      if (!user) return

      try {
        await userManager?.removeUser()
        await userManager?.revokeTokens()
        await userManager?.clearStaleState()
        await userManager?.signoutRedirect()
      } catch (e) {
        console.error('Error signing out: ', e)
      }
    })
    document.addEventListener('visibilitychange', handleRefreshToken)

    return () => {
      document.removeEventListener('visibilitychange', handleRefreshToken)
      sub()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const authenticateWithAppToken = useCallback(
    async (appToken: string) => {
      setBearerToken(appToken)

      setUserData({
        access_token: appToken,
        profile: {},
        expires_at: new Date().getTime() / 1000 + 3600,
      } as User)

      userManager.events.addAccessTokenExpiring(() => {
        setTimeout(() => setBearerToken(appToken), 300)
      })

      setIsLoading(false)
    },
    [userManager]
  )

  useEffect(() => {
    if (window.ReactNativeWebView && window.app_token) {
      authenticateWithAppToken(window.app_token)
      return
    }

    handleRefreshToken()

    const getUser = async (): Promise<void> => {
      /**
       * Check if the user is returning back from OIDC.
       */

      if (hasCodeInUrl(location)) {
        const user = (await userManager.signinCallback()) as User | null
        if (user) setBearerToken(user.access_token)

        setUserData(user)
        setIsLoading(false)
        onSignIn?.(user)
        return
      }

      const user = await userManager!.getUser()
      if (!user && autoSignIn) {
        onBeforeSignIn?.()
        userManager.signinRedirect()
      } else if (user?.expired) {
        userManager
          .signinSilent()
          .then((user) => {
            if (user) setBearerToken(user.access_token)
            setUserData(user)
            setIsLoading(false)
          })
          .catch(() => {
            onBeforeSignIn?.()
            userManager.signinRedirect()
          })
      } else if (isMountedRef.current) {
        if (user) setBearerToken(user.access_token)
        setUserData(user)
        setIsLoading(false)
      }

      return
    }
    getUser()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    location,
    userManager,
    autoSignIn,
    onBeforeSignIn,
    onSignIn,
    authenticateWithAppToken,
  ])

  useEffect(() => {
    const handleTabRefocused = async () => {
      if (document.visibilityState !== 'visible') return
      try {
        const user = await userManager.getUser()
        if (user && user.expired) {
          const newUser = await userManager.signinSilent()
          if (newUser) setBearerToken(newUser.access_token)
          setUserData(newUser)
        }
      } catch (error) {
        console.error('Error refreshing token', error)
      }
    }
    document.addEventListener('visibilitychange', handleTabRefocused)
    return () => {
      document.removeEventListener('visibilitychange', handleTabRefocused)
    }
  }, [userManager, setBearerToken, setUserData, authenticateWithAppToken])

  useEffect(() => {
    const onAccessTokenExpiring = async () => {
      try {
        const user = await userManager.signinSilent()
        if (user) {
          setBearerToken(user.access_token)
          setUserData(user)
          console.debug('[OIDC] Token silently renewed.')
        }
      } catch (error) {
        console.error('[OIDC] Silent renewal failed', error)
        // Optional: fallback to redirect if silent renewal fails
        onBeforeSignIn?.()
        userManager.signinRedirect()
      }
    }

    userManager.events.addAccessTokenExpiring(onAccessTokenExpiring)

    return () => {
      userManager.events.removeAccessTokenExpiring(onAccessTokenExpiring)
    }
  }, [userManager.events, onBeforeSignIn])

  useEffect(() => {
    if (!userData?.expires_at) return

    const now = new Date().getTime() / 1000 // current time in seconds
    const secondsLeft = userData.expires_at - now

    const renewBeforeSeconds = 60 // renew 1 min before expiry
    const renewIn = Math.max(secondsLeft - renewBeforeSeconds, 0)

    const renewTimer = setTimeout(async () => {
      try {
        const newUser = await userManager.signinSilent()
        if (newUser) {
          setBearerToken(newUser.access_token)
          setUserData(newUser)
          console.debug('[OIDC] Token silently renewed via timer.')
        }
      } catch (error) {
        console.error('[OIDC] Timer-based silent renewal failed', error)
      }
    }, renewIn * 1000)

    return () => clearTimeout(renewTimer)
  }, [userData, userManager])

  useEffect(() => {
    // for refreshing react state when new state is available in e.g. session storage
    const updateUserData = async () => {
      const user = await userManager.getUser()
      if (isMountedRef.current) setUserData(user)
    }

    userManager.events.addUserLoaded(updateUserData)

    return () => userManager.events.removeUserLoaded(updateUserData)
  }, [userManager])

  useEffect(() => {
    if (window.ReactNativeWebView) {
      const handleMessage = (event: MessageEvent) => {
        const messageData = JSON.parse(event.data)
        if (messageData.type === 'app_token') {
          authenticateWithAppToken(messageData.token)
        }
      }

      window.addEventListener('message', handleMessage)

      return () => {
        window.removeEventListener('message', handleMessage)
      }
    }
  }, [authenticateWithAppToken])

  return (
    <AuthContext.Provider
      value={{
        signIn: async (args?: SigninRedirectArgs): Promise<void> => {
          await userManager.signinRedirect(args)
        },

        signOut: async (): Promise<void> => {
          setIsLoading(true)
          await signOutHooks()
          await userManager!.removeUser()
          setIsLoading(false)
        },

        signOutRedirect: async (
          args?: BblSignoutRedirectArgs
        ): Promise<void> => {
          setIsLoading(true)
          await signOutHooks()

          // Handle logout manually if on mobile
          if (window.ReactNativeWebView) {
            // Remove anything that has anything to do with a authenticated user.
            await userManager.removeUser()
            await userManager.revokeTokens()

            // Should not silently redirect us back to the page for some random reason.
            await localStorage.removeItem(LOCAL_STORAGE_REDIRECT_URL_LOGOUT)
            await userManager.clearStaleState()

            await userManager!.signoutRedirect(args)
            return
          }

          // Set a redirect url, to be handled in the main index.tsx and ran with redirectWithUri
          // But only if signoutRedirect is called with a ({ post_logout_redirect_uri })
          if (args?.bbl_redirect && args.bbl_redirect !== '') {
            localStorage.setItem(
              LOCAL_STORAGE_REDIRECT_URL_LOGOUT,
              args.bbl_redirect
            )
          }

          await userManager!.signoutRedirect({
            post_logout_redirect_uri: args?.post_logout_redirect_uri,
          })
          setIsLoading(false)
        },

        isAuthenticated,
        userManager,
        userData,
        isLoading,
      }}
    >
      {isLoading && <PageLoader />}

      {!isLoading &&
        (isAuthenticated() ? (
          children
        ) : (
          <NotAuthorizedPage authenticationError />
        ))}
    </AuthContext.Provider>
  )
}
